import { SelectOptionElement } from '@odin-labs/components';
import {
  JobsiteBadgeTemplate,
  WorkerCitizenshipStatus,
  WorkerCardFormat,
  CCureEnvironment,
  WorkerDocumentType,
  WorkerWorkAuthorizationStatus,
} from 'apollo/generated/client-operations';
import { pascalToTrainCase } from 'utils';
import { getSelectOptions } from 'components/utils';
import {
  JobsiteModule,
  JobsiteOnboardingModule,
  JobsiteAppCheckInModule,
} from 'containers/worker/types';

export const getOnboardingModule = (
  modules: JobsiteModule[],
): JobsiteOnboardingModule =>
  modules?.find((m): m is JobsiteOnboardingModule => {
    return m.__typename === 'JobsiteOnboardingModule'; // eslint-disable-line no-underscore-dangle
  });

export const getAppCheckInModule = (
  modules: JobsiteModule[],
): JobsiteAppCheckInModule =>
  modules?.find((m): m is JobsiteAppCheckInModule => {
    return m.__typename === 'JobsiteAppCheckInModule'; // eslint-disable-line no-underscore-dangle
  });

export type WorkerCardFormatForOption = Pick<
  WorkerCardFormat,
  | 'workerCardFormatId'
  | 'name'
  | 'facilityCode'
  | 'encoding'
  | 'cardType'
  | 'validationRegex'
  | 'validationMessage'
>;
export type WorkerCardFormatOption = SelectOptionElement &
  Pick<WorkerCardFormat, 'name' | 'cardType' | 'encoding' | 'validationRegex'>;

export const getWorkerCardFormatOptions = (
  workerCardFormats: WorkerCardFormatForOption[],
): WorkerCardFormatOption[] =>
  workerCardFormats?.map(
    ({
      workerCardFormatId,
      name,
      facilityCode,
      encoding,
      cardType,
      validationRegex,
      validationMessage,
    }) => ({
      value: workerCardFormatId,
      label: [name, facilityCode].filter(Boolean).join(' - '),
      name,
      cardType,
      encoding,
      validationRegex,
      validationMessage,
    }),
  );

export type CCureEnvironmentForOption = Pick<
  CCureEnvironment,
  'cCureEnvironmentId' | 'namespace'
>;

export const getCCureEnvironmentsOptions = (
  cCureEnvironments: CCureEnvironmentForOption[],
): SelectOptionElement[] =>
  cCureEnvironments?.map(({ cCureEnvironmentId: value, namespace: label }) => ({
    value,
    label,
  }));

export type WorkerDocumentTypeForOption = Pick<
  WorkerDocumentType,
  'workerDocumentTypeId' | 'name' | 'key' | 'isPhi'
>;
export type WorkerDocumentTypeOption = SelectOptionElement &
  Pick<WorkerDocumentType, 'key' | 'isPhi'>;
export const getWorkerDocumentTypesOptions = (
  workerDocumentTypes: WorkerDocumentTypeForOption[],
): WorkerDocumentTypeOption[] =>
  workerDocumentTypes?.map(({ workerDocumentTypeId, name, key, isPhi }) => ({
    value: workerDocumentTypeId,
    label: name,
    key,
    isPhi,
  }));

export type JobsiteBadgeTemplateForOption = Pick<
  JobsiteBadgeTemplate,
  'id' | 'isDefault' | 'badgeTemplate'
>;
export type JobsiteBadgeTemplateOption = SelectOptionElement &
  Pick<JobsiteBadgeTemplate, 'id' | 'isDefault'>;
export const getJobsiteBadgeTemplatesOptions = (
  jobsiteBadgeTemplates: JobsiteBadgeTemplateForOption[],
  isForTempBadge: boolean = false,
): JobsiteBadgeTemplateOption[] =>
  jobsiteBadgeTemplates
    ?.filter(
      (template) => template.badgeTemplate.isForTempBadge === isForTempBadge,
    )
    .map(({ isDefault, badgeTemplate }) => ({
      value: badgeTemplate.templateName,
      label: badgeTemplate.displayName,
      isDefault,
      id: badgeTemplate.id,
    }));

export const citizenshipStatusOptions = getSelectOptions({
  values: Object.values(WorkerCitizenshipStatus),
  labelGetter: pascalToTrainCase,
});

const getWorkAuthorizationStatus = (
  status: WorkerWorkAuthorizationStatus | null | undefined,
): string => {
  switch (status) {
    case WorkerWorkAuthorizationStatus.AuthorizedToWorkInTheUs:
      return 'Authorized to work in the US';
    case WorkerWorkAuthorizationStatus.NotAuthorizedToWorkInTheUs:
      return 'Not authorized to work in the US';
    default:
      return '';
  }
};

export const workAuthorizationStatusOptions = getSelectOptions({
  values: Object.values(WorkerWorkAuthorizationStatus),
  labelGetter: getWorkAuthorizationStatus,
});

/**
 * Builds an array of options, including the current value if it's not already
 * present.
 * @param options - The available options.
 * @param currentValue - The current value that may have been selected from an
 * old version of the options.
 * @returns An array of options, including the current value if applicable.
 */
export const buildOptionsWithCurrentValue = (
  options: string[] | undefined,
  currentValue: string | null | undefined,
): string[] => {
  const result = options || [];
  if (!currentValue || result.includes(currentValue)) {
    return result.sort();
  }
  return [...result, currentValue].sort();
};
